import { EditorView } from "@codemirror/view";

/**
 * Base editor chrome theme. All colors come from CSS variables so the editor
 * and the app can never drift apart.
 */
export const editorTheme = EditorView.theme(
  {
    "&": {
      height: "100%",
      fontSize: "var(--fs-editor)",
      backgroundColor: "var(--surface-editor)",
      color: "var(--text-primary)",
    },
    ".cm-scroller": {
      fontFamily: "var(--font-text)",
      lineHeight: "var(--lh-editor)",
      overflow: "auto",
    },
    ".cm-content": {
      // Readable measure, centered, generous vertical padding.
      maxWidth: "var(--editor-measure, 46em)",
      margin: "0 auto",
      padding: "24px 32px 45vh",
      caretColor: "var(--accent)",
      userSelect: "text",
    },
    "&.cm-focused": {
      outline: "none",
      boxShadow: "none",
    },
    // the app-global :focus-visible ring must never wrap the content element
    ".cm-content:focus-visible": {
      boxShadow: "none",
      outline: "none",
    },
    ".cm-line": {
      padding: "0",
    },
    ".cm-cursor": {
      borderLeftColor: "var(--accent)",
      borderLeftWidth: "2px",
    },
    "&.cm-focused > .cm-scroller .cm-selectionLayer .cm-selectionBackground, ::selection": {
      backgroundColor: "var(--accent-selection)",
    },
    ".cm-selectionBackground": {
      backgroundColor: "var(--accent-selection)",
    },
    ".cm-activeLine": {
      backgroundColor: "transparent",
    },
    // --- Search panel / tooltips inherit app look ---
    ".cm-panels": {
      backgroundColor: "var(--surface-panel)",
      color: "var(--text-primary)",
      borderTop: "1px solid var(--border)",
    },
    ".cm-tooltip": {
      backgroundColor: "var(--surface-raised)",
      border: "none",
      borderRadius: "var(--radius-md)",
      boxShadow: "var(--shadow-menu)",
      color: "var(--text-primary)",
    },

    // ---- autocomplete (slash menu + wikilinks) ----
    ".cm-tooltip.cm-tooltip-autocomplete": {
      borderRadius: "var(--radius-lg)",
      padding: "4px",
      minWidth: "240px",
    },
    ".cm-tooltip.cm-tooltip-autocomplete > ul": {
      fontFamily: "var(--font-ui)",
      fontSize: "var(--fs-ui)",
      maxHeight: "320px",
      maxWidth: "420px",
    },
    ".cm-tooltip-autocomplete > ul > li": {
      display: "flex",
      alignItems: "center",
      gap: "10px",
      padding: "5px 10px",
      borderRadius: "var(--radius-sm)",
      lineHeight: "1.3",
      color: "var(--text-primary)",
    },
    ".cm-tooltip-autocomplete > ul > li[aria-selected]": {
      backgroundColor: "var(--accent-subtle)",
      color: "var(--text-primary)",
    },
    ".cm-completionLabel": {
      flex: "1",
    },
    ".cm-completionMatchedText": {
      textDecoration: "none",
      color: "var(--accent-text)",
      fontWeight: "650",
    },
    ".cm-completionDetail": {
      marginLeft: "auto",
      fontStyle: "normal",
      fontFamily: "var(--font-mono)",
      fontSize: "var(--fs-xs)",
      color: "var(--text-muted)",
      background: "var(--surface-active)",
      borderRadius: "3px",
      padding: "1px 6px",
    },
    ".cm-tooltip-autocomplete .cm-completionSection": {
      display: "block",
      padding: "8px 10px 3px",
      fontSize: "var(--fs-xs)",
      fontWeight: "600",
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      color: "var(--text-muted)",
      borderTop: "1px solid var(--border)",
      marginTop: "4px",
    },
    ".cm-tooltip-autocomplete .cm-completionSection:first-of-type": {
      borderTop: "none",
      marginTop: "0",
      paddingTop: "4px",
    },

    // ============ Live-preview construct styles ============
    ".hn-h1": { fontSize: "1.7em", fontWeight: "700", lineHeight: "1.3", color: "var(--md-heading)" },
    ".hn-h2": { fontSize: "1.45em", fontWeight: "650", lineHeight: "1.32", color: "var(--md-heading)" },
    ".hn-h3": { fontSize: "1.25em", fontWeight: "650", lineHeight: "1.35", color: "var(--md-heading)" },
    ".hn-h4": { fontSize: "1.1em", fontWeight: "600", color: "var(--md-heading)" },
    ".hn-h5": { fontSize: "1em", fontWeight: "600", color: "var(--md-heading)" },
    ".hn-h6": { fontSize: "0.9em", fontWeight: "600", color: "var(--text-secondary)" },
    ".hn-subtext": { fontSize: "0.8em", color: "var(--md-subtext)" },

    ".hn-blockquote": {
      borderLeft: "2px solid var(--md-quote-bar)",
      paddingLeft: "12px",
      color: "var(--md-quote)",
    },

    ".hn-codeblock": {
      backgroundColor: "var(--md-code-bg)",
      fontFamily: "var(--font-mono)",
      fontSize: "0.86em",
      lineHeight: "1.55",
      padding: "0 12px",
    },
    ".hn-codeblock-first": {
      borderRadius: "var(--radius-md) var(--radius-md) 0 0",
      paddingTop: "6px",
    },
    ".hn-codeblock-last": {
      borderRadius: "0 0 var(--radius-md) var(--radius-md)",
      paddingBottom: "6px",
    },
    ".hn-fence": {
      color: "var(--text-muted)",
    },

    ".hn-hr-widget": {
      display: "inline-block",
      width: "100%",
      verticalAlign: "middle",
    },
    ".hn-hr-widget hr": {
      border: "none",
      borderTop: "1px solid var(--md-hr)",
      margin: "0.7em 0",
    },

    ".hn-checkbox": {
      display: "inline-flex",
      alignItems: "center",
      verticalAlign: "text-bottom",
      cursor: "pointer",
      marginRight: "6px",
    },
    ".hn-checkbox input": {
      accentColor: "var(--md-checkbox)",
      width: "14px",
      height: "14px",
      margin: "0",
      cursor: "pointer",
    },
    ".hn-task-done": {
      color: "var(--text-muted)",
      textDecoration: "line-through",
      textDecorationColor: "var(--text-disabled)",
    },

    ".hn-list-bullet": {
      color: "var(--accent-text)",
      fontWeight: "700",
    },

    ".hn-image-widget": {
      display: "block",
      maxWidth: "100%",
    },
    ".hn-image-widget img": {
      maxWidth: "100%",
      maxHeight: "480px",
      borderRadius: "var(--radius-md)",
      display: "block",
      margin: "4px 0",
    },
    ".hn-image-broken": {
      color: "var(--text-muted)",
      fontSize: "0.85em",
      fontStyle: "italic",
    },

    // ---- presence cursors (your other devices) ----
    ".cm-ySelection": {
      // y-codemirror sets backgroundColor inline from the user color
      opacity: "0.45",
    },
    ".cm-ySelectionCaret": {
      position: "relative",
      borderLeft: "1.5px solid",
      marginLeft: "-0.75px",
    },
    ".cm-ySelectionInfo": {
      position: "absolute",
      top: "-1.35em",
      left: "-1.5px",
      fontFamily: "var(--font-ui)",
      fontSize: "10px",
      fontWeight: "600",
      lineHeight: "1.2",
      padding: "1px 5px",
      borderRadius: "3px 3px 3px 0",
      whiteSpace: "nowrap",
      color: "#04231a",
      userSelect: "none",
      pointerEvents: "none",
      zIndex: "10",
    },
  },
  { dark: true },
);
